import { Box, Container, Typography, Chip, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
// import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const projects = [
  {
    title: 'The Barber Shop',
    category: 'Booking Site',
    description:
      'Online booking platform for a premium barbershop. Customers can schedule appointments, view services, and pay online.',
    tags: ['React', 'Booking API', 'Stripe'],
    gradient: 'linear-gradient(135deg, #1a0533 0%, #2d1b69 40%, #0d0d1a 100%)',
    accentColor: '#7C3AED',
    wireframe: 'barber',
  },
  {
    title: 'Paws & Go',
    category: 'Dog Walker App',
    description:
      'Mobile-first PWA for dog walking services. GPS tracking, recurring bookings, and walker profiles.',
    tags: ['React', 'Maps API', 'PWA'],
    gradient: 'linear-gradient(135deg, #0a2e1a 0%, #065f35 40%, #0a2e1a 100%)',
    accentColor: '#10B981',
    wireframe: 'paws',
  },
  {
    title: 'Spice Garden',
    category: 'Food Ordering',
    description:
      'Full-stack food ordering website for a local Indian takeaway. Menu management, cart, and real-time order tracking.',
    tags: ['React', 'Node.js', 'Stripe'],
    gradient: 'linear-gradient(135deg, #2d0a00 0%, #7c1a00 40%, #1a0500 100%)',
    accentColor: '#F59E0B',
    wireframe: 'food',
  },
  {
    title: 'FitLife Gym',
    category: 'Membership Dashboard',
    description:
      'Member dashboard for a local gym. Workout tracking, class bookings, membership billing, and progress charts.',
    tags: ['React', 'Chart.js', 'Auth'],
    gradient: 'linear-gradient(135deg, #001a2d 0%, #003366 40%, #001020 100%)',
    accentColor: '#06B6D4',
    wireframe: 'gym',
  },
  {
    title: 'Bloom Florist',
    category: 'E-Commerce',
    description:
      'Beautiful e-commerce store for a boutique florist. Product gallery, custom arrangements, and same-day delivery.',
    tags: ['React', 'Shopify', 'CMS'],
    gradient: 'linear-gradient(135deg, #1a0028 0%, #6b21a8 40%, #1a0028 100%)',
    accentColor: '#E879F9',
    wireframe: 'florist',
  },
  {
    title: 'TechStart Dashboard',
    category: 'Analytics Dashboard',
    description:
      'Real-time analytics dashboard for a startup. KPI tracking, user metrics, revenue charts, and team collaboration.',
    tags: ['React', 'D3.js', 'WebSockets'],
    gradient: 'linear-gradient(135deg, #0d1117 0%, #1e293b 40%, #0f172a 100%)',
    accentColor: '#38BDF8',
    wireframe: 'dashboard',
  },
];

const WireframeMock = ({ type, accentColor }) => {
  const barberMock = (
    <Box
      sx={{
        p: 1.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box
        sx={{
          background: 'rgba(255,255,255,0.06)',
          borderRadius: 1,
          p: 1,
          mb: 0.5,
        }}
      >
        <Box
          sx={{
            width: '50%',
            height: 6,
            background: accentColor,
            borderRadius: 1,
            opacity: 0.9,
          }}
        />
        <Box
          sx={{
            width: '30%',
            height: 4,
            background: 'rgba(255,255,255,0.3)',
            borderRadius: 1,
            mt: 0.5,
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
        <Box
          sx={{
            flex: 1,
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 1,
            p: 0.75,
          }}
        >
          {['Mon', 'Tue', 'Wed'].map((d) => (
            <Box
              key={d}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 4,
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: 1,
                }}
              />
              <Box
                sx={{
                  flex: 1,
                  height: 4,
                  background: `${accentColor}40`,
                  borderRadius: 1,
                }}
              />
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            width: '45%',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 1,
            p: 0.75,
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: 4,
              background: 'rgba(255,255,255,0.15)',
              borderRadius: 1,
              mb: 0.5,
            }}
          />
          <Box
            sx={{
              width: '80%',
              height: 4,
              background: 'rgba(255,255,255,0.1)',
              borderRadius: 1,
              mb: 1,
            }}
          />
          <Box
            sx={{
              width: '100%',
              height: 18,
              background: `${accentColor}`,
              borderRadius: 1,
              opacity: 0.85,
            }}
          />
        </Box>
      </Box>
    </Box>
  );

  const dashboardMock = (
    <Box
      sx={{
        p: 1.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1 }}>
        {[80, 55, 70, 90].map((h, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: 6,
                background: 'rgba(255,255,255,0.12)',
                borderRadius: 1,
              }}
            />
            <Box
              sx={{
                width: '60%',
                height: 4,
                background: `${accentColor}70`,
                borderRadius: 1,
              }}
            />
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          flex: 1,
          background: 'rgba(255,255,255,0.04)',
          borderRadius: 1,
          p: 0.75,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <svg
          width='100%'
          height='50'
          viewBox='0 0 100 50'
          preserveAspectRatio='none'
        >
          <polyline
            points='0,40 20,25 40,30 60,10 80,20 100,5'
            fill='none'
            stroke={accentColor}
            strokeWidth='2'
            strokeOpacity='0.8'
          />
          <polygon
            points='0,40 20,25 40,30 60,10 80,20 100,5 100,50 0,50'
            fill={accentColor}
            fillOpacity='0.1'
          />
        </svg>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Box
          sx={{
            flex: 1,
            height: 16,
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 1,
          }}
        />
        <Box
          sx={{
            flex: 1,
            height: 16,
            background: `${accentColor}20`,
            borderRadius: 1,
          }}
        />
      </Box>
    </Box>
  );

  const foodMock = (
    <Box
      sx={{
        p: 1.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box
        sx={{
          height: 30,
          background: `linear-gradient(90deg, ${accentColor}40, transparent)`,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          px: 1,
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 4,
            background: accentColor,
            borderRadius: 1,
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Box
          sx={{
            width: 20,
            height: 4,
            background: 'rgba(255,255,255,0.3)',
            borderRadius: 1,
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 0.75,
          flex: 1,
        }}
      >
        {[1, 2, 3, 4].map((i) => (
          <Box
            key={i}
            sx={{
              background: 'rgba(255,255,255,0.05)',
              borderRadius: 1,
              p: 0.75,
            }}
          >
            <Box
              sx={{
                height: 20,
                background: `${accentColor}20`,
                borderRadius: 0.5,
                mb: 0.5,
              }}
            />
            <Box
              sx={{
                width: '80%',
                height: 3,
                background: 'rgba(255,255,255,0.2)',
                borderRadius: 1,
                mb: 0.25,
              }}
            />
            <Box
              sx={{
                width: '50%',
                height: 3,
                background: `${accentColor}80`,
                borderRadius: 1,
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );

  const genericMock = (
    <Box
      sx={{
        p: 1.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box
        sx={{
          height: 28,
          background: `${accentColor}15`,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          px: 1,
          gap: 1,
          border: `1px solid ${accentColor}30`,
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 4,
            background: accentColor,
            borderRadius: 1,
            opacity: 0.8,
          }}
        />
        <Box sx={{ flex: 1 }} />
        {[1, 2, 3].map((i) => (
          <Box
            key={i}
            sx={{
              width: 16,
              height: 4,
              background: 'rgba(255,255,255,0.15)',
              borderRadius: 1,
            }}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
        <Box
          sx={{
            width: '30%',
            background: 'rgba(255,255,255,0.04)',
            borderRadius: 1,
            p: 0.5,
          }}
        >
          {[70, 50, 60, 45, 55].map((w, i) => (
            <Box
              key={i}
              sx={{
                width: `${w}%`,
                height: 4,
                background: i === 0 ? accentColor : 'rgba(255,255,255,0.1)',
                borderRadius: 1,
                mb: 0.5,
                opacity: i === 0 ? 0.9 : 1,
              }}
            />
          ))}
        </Box>
        <Box
          sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}
        >
          {[1, 2, 3].map((i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                background: 'rgba(255,255,255,0.04)',
                borderRadius: 1,
                border: `1px solid ${accentColor}20`,
              }}
            />
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          height: 18,
          background: `linear-gradient(90deg, ${accentColor}, ${accentColor}60)`,
          borderRadius: 1,
          opacity: 0.85,
        }}
      />
    </Box>
  );

  const mocks = {
    barber: barberMock,
    dashboard: dashboardMock,
    food: foodMock,
    paws: genericMock,
    gym: genericMock,
    florist: genericMock,
  };

  return mocks[type] || genericMock;
};

const sectionVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

export default function Portfolio() {
  return (
    <Box
      id='portfolio'
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(180deg, #0F0F1A 0%, #111120 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background accent */}
      <Box
        sx={{
          position: 'absolute',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: '#7C3AED',
          filter: 'blur(150px)',
          opacity: 0.07,
          top: '30%',
          left: '50%',
          transform: 'translateX(-50%)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth='lg'>
        {/* Section header */}
        <motion.div
          variants={sectionVariants}
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.3 }}
        >
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Box
              sx={{
                display: 'inline-block',
                px: 2,
                py: 0.5,
                borderRadius: 50,
                background: 'rgba(124,58,237,0.12)',
                border: '1px solid rgba(124,58,237,0.3)',
                mb: 2,
              }}
            >
              <Typography
                variant='overline'
                sx={{ color: '#A78BFA', fontWeight: 700, letterSpacing: 2 }}
              >
                Portfolio
              </Typography>
            </Box>
            <Typography
              variant='h2'
              sx={{
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 800,
                mb: 2,
              }}
            >
              Example{' '}
              <Box component='span' className='gradient-text'>
                Projects
              </Box>
            </Typography>
            <Typography
              variant='body1'
              sx={{
                color: 'rgba(255,255,255,0.55)',
                maxWidth: 500,
                mx: 'auto',
                fontSize: '1.05rem',
              }}
            >
              Real-world websites built for small businesses — each one crafted
              to drive results.
            </Typography>
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={24}
            slidesPerView={1}
            navigation
            pagination={{ clickable: true }}
            autoplay={{ delay: 4500, disableOnInteraction: false }}
            breakpoints={{
              640: { slidesPerView: 1.5 },
              900: { slidesPerView: 2 },
              1200: { slidesPerView: 3 },
            }}
            style={{ paddingBottom: '50px' }}
          >
            {projects.map((project) => (
              <SwiperSlide key={project.title}>
                <Box
                  sx={{
                    borderRadius: '16px',
                    overflow: 'hidden',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.35s ease',
                    height: '100%',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 20px 50px ${project.accentColor}30`,
                      border: `1px solid ${project.accentColor}40`,
                    },
                  }}
                >
                  {/* Mock screenshot */}
                  <Box
                    sx={{
                      height: 180,
                      background: project.gradient,
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Browser chrome top bar */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 22,
                        background: 'rgba(0,0,0,0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        px: 1,
                        gap: 0.5,
                        zIndex: 2,
                      }}
                    >
                      {['#ff5f57', '#febc2e', '#28c840'].map((c) => (
                        <Box
                          key={c}
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: c,
                          }}
                        />
                      ))}
                      <Box
                        sx={{
                          flex: 1,
                          mx: 1,
                          height: 10,
                          background: 'rgba(255,255,255,0.08)',
                          borderRadius: 5,
                        }}
                      />
                    </Box>
                    {/* Wireframe content */}
                    <Box sx={{ pt: '22px', height: '100%' }}>
                      <WireframeMock
                        type={project.wireframe}
                        accentColor={project.accentColor}
                      />
                    </Box>
                    {/* Category chip overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 30,
                        right: 10,
                        px: 1.5,
                        py: 0.4,
                        borderRadius: 50,
                        background: `${project.accentColor}25`,
                        border: `1px solid ${project.accentColor}50`,
                        backdropFilter: 'blur(8px)',
                      }}
                    >
                      <Typography
                        variant='caption'
                        sx={{
                          color: project.accentColor,
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          letterSpacing: 0.5,
                        }}
                      >
                        {project.category}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Card content */}
                  <Box sx={{ p: 2.5 }}>
                    <Typography
                      variant='h6'
                      sx={{ fontWeight: 700, mb: 0.75, fontSize: '1.05rem' }}
                    >
                      {project.title}
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        color: 'rgba(255,255,255,0.55)',
                        fontSize: '0.85rem',
                        lineHeight: 1.6,
                        mb: 2,
                      }}
                    >
                      {project.description}
                    </Typography>
                    <Stack
                      direction='row'
                      spacing={0.75}
                      flexWrap='wrap'
                      sx={{ mb: 2.5, gap: 0.75 }}
                    >
                      {project.tags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size='small'
                          sx={{
                            background: `${project.accentColor}15`,
                            color: project.accentColor,
                            border: `1px solid ${project.accentColor}30`,
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            height: 22,
                          }}
                        />
                      ))}
                    </Stack>
                    {/* <Button
                      variant="outlined"
                      size="small"
                      endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                      fullWidth
                      sx={{
                        borderColor: `${project.accentColor}50`,
                        color: project.accentColor,
                        fontSize: '0.8rem',
                        py: 0.75,
                        '&:hover': {
                          background: `${project.accentColor}15`,
                          borderColor: project.accentColor,
                        },
                      }}
                    >
                      View Project
                    </Button> */}
                  </Box>
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </motion.div>
      </Container>
    </Box>
  );
}
